import { Toast } from '@base-ui/react/toast'
import { CircleCheck } from 'lucide-react'
import type { ReactNode } from 'react'

import s from './Toast.module.css'

/**
 * Le toast du prototype validé (`toast(msg, sub)`, `mocks/ghp-bo/app.js` — identique au terrain) :
 * la confirmation d'un geste qui a abouti, en bas de l'écran, qui s'efface d'elle-même.
 *
 * Le comportement vient de la primitive `Toast` de Base UI (règle 3) : minuterie, annonce polie
 * aux lecteurs d'écran, région atteignable au clavier (F6). L'apparence est celle du prototype —
 * une pile centrée, pas l'empilement en éventail de la démonstration de la primitive.
 *
 * ⚠️ **Il confirme, il ne signale pas un échec.** Le prototype ne l'emploie que pour ce qui a
 * abouti (« Contrôle archivé », « Lot exporté »), et son icône est une coche. Un refus se dit là où
 * le geste a été tenté, dans les mots du serveur — un toast qui s'efface le ferait disparaître.
 */

/** Les 3,2 s du prototype (`setTimeout(…, 3200)`). */
const DURATION = 3200

/**
 * ⚠️ **Un gestionnaire global, pas le crochet de la primitive** : `Toast.useToastManager()` lève
 * hors de son fournisseur, et chaque écran monté seul dans un test — c'est la règle des écrans
 * présentationnels — casserait pour une confirmation qu'il ne vérifie pas. Le gestionnaire global
 * accepte un toast sans fournisseur monté : il ne s'affiche simplement nulle part.
 */
const manager = Toast.createToastManager()

export interface ToastProviderProps {
  children?: ReactNode
}

/** À poser une fois, autour de l'application : les toasts s'affichent tous dans la même pile. */
export function ToastProvider({ children }: ToastProviderProps) {
  return (
    <Toast.Provider timeout={DURATION} toastManager={manager}>
      {children}
      <Toast.Portal>
        <Toast.Viewport className={s.viewport}>
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  )
}

function ToastList() {
  const { toasts } = Toast.useToastManager()

  return toasts.map((toast) => (
    <Toast.Root key={toast.id} toast={toast} className={s.toast}>
      <CircleCheck className={s.icon} size={20} strokeWidth={2} aria-hidden />
      <div>
        <Toast.Title className={s.title} />
        {toast.description !== undefined && <Toast.Description className={s.detail} />}
      </div>
    </Toast.Root>
  ))
}

/**
 * Affiche un toast : un titre, et une précision en chasse fixe — le `sub` du prototype, qui y met
 * une référence, un nom de fichier, un nombre.
 */
export function useToast(): (title: ReactNode, detail?: ReactNode) => void {
  return showToast
}

function showToast(title: ReactNode, detail?: ReactNode): void {
  manager.add({ title, description: detail })
}
